import type { Activity, Matter, Task } from "@/shared/cache";
import {
	getFirmDefaults,
	getFirmDomain,
	isActivityRequired,
	isGouldratner,
	isNamanHowell,
	isTaskRequired,
} from "@/shared/utils/firm";
import { applicableMatterCodes } from "./matterBillingCodes";

/** Normalize cached rows too; a matter can arrive or change after its entries. */
export function visibleMatterCodes<
	T extends {
		task: string | null;
		activity: string | null;
		taskId?: string | null;
		activityId?: string | null;
	},
>(codes: T, matter: Matter | undefined): T {
	if (!matter) return codes;
	const visible = applicableMatterCodes(
		getFirmDomain(),
		matter,
		codes.task,
		codes.activity,
	);
	const ids = applicableMatterCodes(
		getFirmDomain(),
		matter,
		codes.taskId ?? null,
		codes.activityId ?? null,
	);
	if (
		visible.task === codes.task &&
		visible.activity === codes.activity &&
		ids.task === (codes.taskId ?? null) &&
		ids.activity === (codes.activityId ?? null)
	)
		return codes;
	return {
		...codes,
		task: visible.task,
		activity: visible.activity,
		taskId: ids.task,
		activityId: ids.activity,
	};
}

/**
 * Firm-aware task list. When a matter is selected, scope to that matter's
 * `task_group`; a selected matter with no task group gets no task options.
 * If the matter is not hydrated yet, keep the broad list so forms do not clear
 * valid prefilled codes before the matter cache settles. If no matter is
 * selected, fall back to the firm's default task group when one exists;
 * otherwise show the full list.
 *
 * Shared between EntryForm and the merge/group dialogs so a matter chip in
 * the dialog narrows the task picker the same way it does in add/edit.
 */
export function availableTasksForMatter(
	tasks: Task[],
	matters: Matter[],
	matterId: string | null | undefined,
): Task[] {
	const selected = matterId
		? (matters.find((m) => m.matter_id === matterId) ?? null)
		: null;
	if (matterId && !selected) return tasks;

	const selectedGroup = selected?.task_group?.trim();

	if (matterId && !isTaskRequired(matterId, matters)) return [];

	if (selectedGroup) {
		return tasks.filter((t) => t.task_group === selectedGroup);
	}

	const defaultGroup = getFirmDefaults().taskGroup;
	if (defaultGroup) return tasks.filter((t) => t.task_group === defaultGroup);

	return tasks;
}

/**
 * Firm-aware activity list. When a matter is selected, scope to that matter's
 * `activity_group`; a selected matter with no activity group gets no activity
 * options. If the matter is not hydrated yet, keep the broad list so forms do
 * not clear valid prefilled codes before the matter cache settles. Gould Ratner
 * uses the firm's default activity group for options because its required
 * activity rule is driven by `task_group`. Naman Howell intentionally keeps the
 * global activity list for the same required-rule reason, but without a default
 * activity group. If no matter is selected, fall back to the firm's default
 * activity group when one exists; otherwise show the full list.
 */
export function availableActivitiesForMatter(
	activities: Activity[],
	matters: Matter[],
	matterId: string | null | undefined,
): Activity[] {
	const selected = matterId
		? (matters.find((m) => m.matter_id === matterId) ?? null)
		: null;
	if (matterId && !selected) return activities;
	if (matterId && !isActivityRequired(matterId, matters)) return [];

	const selectedGroup = selected?.activity_group?.trim();
	const defaultGroup = getFirmDefaults().activityGroup;

	if (isGouldratner() && defaultGroup) {
		const defaultActivities = activities.filter(
			(a) => a.activity_group === defaultGroup,
		);
		return defaultActivities.length > 0 ? defaultActivities : activities;
	}

	if (isNamanHowell()) return activities;

	if (selectedGroup) {
		return activities.filter((a) => a.activity_group === selectedGroup);
	}

	if (defaultGroup) {
		return activities.filter((a) => a.activity_group === defaultGroup);
	}

	return activities;
}
